<template>
    <i
        :style="{ 'min-width': width, height: 'auto' }"
        :class="cssClasses"
        v-bind="$attrs"
        :data-icon="icon"
        v-html="content"
    />
</template>

<script lang="ts" setup>
    import { IconType } from '@/enums/ui.enum';

    const props = withDefaults(
        defineProps<{
            css?: string;
            icon: string;
            type?: `${IconType}`;
            size?: number | string;
            color?: string;
            hover?: boolean;
        }>(),
        {
            css: '',
            size: 24,
            type: IconType.Common,
            color: 'blue',
            hover: false,
        },
    );

    defineOptions({ inheritAttrs: false });

    const content = ref<string>('');

    const isExternal = computed<boolean>(() => ![IconType.Common, IconType.Service].includes(props.type as any));

    const width = computed<string>(() => (typeof props.size === 'number' ? `${props.size}px` : props.size));

    const getContent = async (icon: string = props.icon): Promise<string> => {
        const module = await import(`../../../assets/icons/${props.type}/icon-${icon}.svg?raw`);

        return module.default;
    };

    const cssClasses = computed<string>(() => {
        let string = 'ui-icon';

        if (props.type !== IconType.Common) {
            string += ` ${props.type}`;
        }

        if (isExternal.value) {
            string += ` ${props.icon}`;
        }

        string += props.hover ? ' hover:fill-current' : props.color ? ` fill-f-${props.color}` : '';

        if (props.css) {
            string += ` ${props.css}`;
        }

        return string;
    });

    watch(
        () => props.icon,
        async (newIcon: string, oldIcon: string) => {
            if (newIcon === oldIcon) {
                return;
            }

            content.value = await getContent(newIcon);
        },
    );

    onBeforeMount(async () => {
        content.value = await getContent();
    });
</script>

<style lang="scss" scoped>
    @use '@/assets/scss/imports';

    .ui-icon {
        box-sizing: content-box;
        display: inline-block;
        overflow: hidden;
        transition: fill 0.3s ease;
    }
</style>
